/**
 * Products Store
 *
 * Manages product list, filters, pagination, individual product profiles,
 * and reference lookup data (brands, categories, forms, manufacturers).
 *
 * All data is sourced from the tms-api-products service via the dedicated
 * products API client defined in @/modules/products/services/products.api.ts.
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import type {
  Product,
  ProductStatus,
  ProductViewFilter,
  CreateProductRequest,
  UpdateProductRequest,
} from '@/modules/products/types';
import type {
  ApiProductDetail,
  ApiLookupItem,
  ApiProductStatus,
} from '@/modules/products/types/product-api.types';
import {
  fetchProducts as apiFetchProducts,
  fetchProductById as apiFetchProductById,
  createProduct as apiCreateProduct,
  updateProduct as apiUpdateProduct,
  fetchLookups as apiFetchLookups,
  uploadProductImage as apiUploadProductImage,
} from '@/modules/products/services/products.api';
import {
  apiProductToViewModel,
  buildCreateProductRequest,
  buildUpdateProductRequest,
  buildUpdateProductFromForm,
  viewStatusToApiStatus,
  apiDetailToFormPayload,
} from '@/modules/products/services/product.transforms';
import type { ProductRegistrationPayload } from '@/modules/products/types/product-form';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { logAuditEvent } from '@/core/services';

export type { ProductViewFilter };

export interface ProductsQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  statusFilters?: ProductStatus[];
  brandFilters?: string[];
  formFilters?: string[];
  viewFilter?: ProductViewFilter;
}

interface LookupState {
  brands: ApiLookupItem[];
  categories: ApiLookupItem[];
  forms: ApiLookupItem[];
  manufacturers: ApiLookupItem[];
}

interface ProductsState {
  // -- List --
  products: Product[];
  total: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  selectedProductIds: Set<string>;
  activeView: ProductViewFilter;
  activeSearch: string;
  activeStatusFilters: ProductStatus[];
  activeBrandFilters: string[];
  activeFormFilters: string[];
  // -- Profile (single product detail) --
  profile: ApiProductDetail | null;
  isLoadingProfile: boolean;
  profileError: string | null;
  // -- Create / Update --
  isCreating: boolean;
  isUpdating: boolean;
  // -- Lookups --
  lookups: LookupState;
  isLoadingLookups: boolean;
  isLoadingBrands: boolean;
}

export const useProductsStore = defineStore('products', {
  state: (): ProductsState => ({
    // List
    products: [],
    total: 0,
    currentPage: 1,
    pageSize: 25,
    isLoading: false,
    isLoadingMore: false,
    hasMore: false,
    error: null,
    selectedProductIds: new Set(),
    activeView: 'all',
    activeSearch: '',
    activeStatusFilters: [],
    activeBrandFilters: [],
    activeFormFilters: [],
    // Profile
    profile: null,
    isLoadingProfile: false,
    profileError: null,
    // Mutations
    isCreating: false,
    isUpdating: false,
    // Lookups
    lookups: {
      brands: [],
      categories: [],
      forms: [],
      manufacturers: [],
    },
    isLoadingLookups: false,
    isLoadingBrands: false,
  }),

  getters: {
    hasProducts: (state): boolean => state.products.length > 0,
    totalPages: (state): number => Math.ceil(state.total / state.pageSize),
    selectedCount: (state): number => state.selectedProductIds.size,
    selectedProducts: (state): Product[] =>
      state.products.filter((p) => state.selectedProductIds.has(p.id)),

    allProductsTotal(): number {
      return this.total;
    },

    activeProductsTotal(): number {
      return this.products.filter((p) => p.status === 'active').length;
    },

    /** Unique brand names from lookup data (falls back to list data). */
    availableBrands: (state): string[] => {
      if (state.lookups.brands.length > 0) {
        return state.lookups.brands.map((b) => b.name).sort();
      }
      return [...new Set(state.products.map((p) => p.brand).filter(Boolean))].sort();
    },

    /** Unique form names from lookup data (falls back to list data). */
    availableForms: (state): string[] => {
      if (state.lookups.forms.length > 0) {
        return state.lookups.forms.map((f) => f.name).sort();
      }
      return [...new Set(state.products.map((p) => p.form).filter(Boolean) as string[])].sort();
    },

    /**
     * Returns the measurement_metric for a given form name (from lookup data).
     * e.g. 'Kcal/ml' or 'Kcal/gram'. Returns undefined if not found.
     */
    measurementMetricForForm: (state) => (formName: string): string | undefined => {
      const item = state.lookups.forms.find(
        (f) => f.name.toLowerCase() === formName.toLowerCase(),
      );
      return item?.measurement_metric;
    },

    /** Unique manufacturer names from lookup data (falls back to list data). */
    availableManufacturers: (state): string[] => {
      if (state.lookups.manufacturers.length > 0) {
        return state.lookups.manufacturers.map((m) => m.name).sort();
      }
      return [
        ...new Set(
          state.products.map((p) => p.manufacturer).filter(Boolean) as string[],
        ),
      ].sort();
    },

    /** Unique category names from lookup data (falls back to list data). */
    availableCategories: (state): string[] => {
      if (state.lookups.categories.length > 0) {
        return state.lookups.categories.map((c) => c.name).sort();
      }
      return [
        ...new Set(
          state.products.map((p) => p.category).filter(Boolean) as string[],
        ),
      ].sort();
    },

    /** View-model of the currently loaded profile. */
    profileAsViewModel: (state): Product | null => {
      if (!state.profile) return null;
      return apiProductToViewModel(state.profile);
    },
  },

  actions: {
    // -------------------------------------------------------------------------
    // Fetch product list
    // -------------------------------------------------------------------------

    /**
     * Fetch products from the API with filtering, search, sorting, and pagination.
     * Replaces the current list in the store.
     */
    async fetchProducts(params: ProductsQueryParams = {}): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      // Persist active filter state for re-use across navigations
      if (params.viewFilter !== undefined) this.activeView = params.viewFilter;
      if (params.search !== undefined) this.activeSearch = params.search;
      if (params.statusFilters !== undefined) this.activeStatusFilters = params.statusFilters;
      if (params.brandFilters !== undefined) this.activeBrandFilters = params.brandFilters;
      if (params.formFilters !== undefined) this.activeFormFilters = params.formFilters;

      try {
        const page = params.page ?? this.currentPage;
        const limit = params.pageSize ?? this.pageSize;

        // Resolve each filter, falling back to the persisted active state so that
        // internal refreshes (e.g. after create/update) keep the current filters
        // unless the caller explicitly overrides them.
        const statusFilters = params.statusFilters ?? this.activeStatusFilters;
        const brandFilters = params.brandFilters ?? this.activeBrandFilters;
        const formFilters = params.formFilters ?? this.activeFormFilters;

        // Map view ProductStatus filter -> ApiProductStatus.
        // The API accepts a single status value; the UI enforces single-select (radio).
        const statusParam: ApiProductStatus | undefined =
          statusFilters.length > 0
            ? viewStatusToApiStatus(statusFilters[0])
            : undefined;

        // Build is_active flag from viewFilter
        let isActiveParam: 'true' | 'false' | undefined;
        const view = params.viewFilter ?? this.activeView;
        if (view === 'active') isActiveParam = 'true';
        else if (view === 'inactive') isActiveParam = 'false';

        const response = await apiFetchProducts({
          search: (params.search ?? this.activeSearch) || undefined,
          status: statusParam,
          is_active: isActiveParam,
          brand_name: brandFilters.length === 1 ? brandFilters[0] : undefined,
          form_name: formFilters.length === 1 ? formFilters[0] : undefined,
          sort_by: 'created_at',
          sort_dir: 'desc',
          page,
          limit,
        });

        // response.data is the unwrapped items array (createApiClient strips the envelope)
        // response.pagination is set by the pre-interceptor in getApiClient()
        const rawItems = response.data;
        const pagination = (response as unknown as Record<string, unknown>).pagination as
          import('@/modules/products/types/product-api.types').ApiPagination | undefined;

        this.products = rawItems.map((item) => apiProductToViewModel(item));
        this.total = pagination?.total ?? rawItems.length;
        this.currentPage = pagination?.page ?? page;
        this.pageSize = pagination?.limit ?? limit;
        this.hasMore = pagination ? pagination.page < pagination.totalPages : false;

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to fetch products');
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    // -------------------------------------------------------------------------
    // Load more (infinite scroll)
    // -------------------------------------------------------------------------

    /** Load the next page and append results to the current list. */
    async loadMoreProducts(params: ProductsQueryParams = {}): Promise<boolean> {
      if (!this.hasMore || this.isLoadingMore) return false;

      this.isLoadingMore = true;
      try {
        const nextPage = this.currentPage + 1;
        const limit = this.pageSize;

        // Resolve active filter state, allowing callers to override via params
        const search = (params.search !== undefined ? params.search : this.activeSearch) || undefined;
        const statusFilters = params.statusFilters ?? this.activeStatusFilters;
        const brandFilters = params.brandFilters ?? this.activeBrandFilters;
        const formFilters = params.formFilters ?? this.activeFormFilters;
        const view = params.viewFilter ?? this.activeView;

        // Map view ProductStatus filter -> ApiProductStatus (same as fetchProducts).
        // The API accepts a single status value; the UI enforces single-select (radio).
        const statusParam: ApiProductStatus | undefined =
          statusFilters.length > 0 ? viewStatusToApiStatus(statusFilters[0]) : undefined;

        // Build is_active flag from viewFilter (same as fetchProducts)
        let isActiveParam: 'true' | 'false' | undefined;
        if (view === 'active') isActiveParam = 'true';
        else if (view === 'inactive') isActiveParam = 'false';

        const response = await apiFetchProducts({
          search,
          status: statusParam,
          is_active: isActiveParam,
          brand_name: brandFilters.length === 1 ? brandFilters[0] : undefined,
          form_name: formFilters.length === 1 ? formFilters[0] : undefined,
          sort_by: 'created_at',
          sort_dir: 'desc',
          page: nextPage,
          limit,
        });

        const rawItems = response.data;
        const pagination = (response as unknown as Record<string, unknown>).pagination as
          import('@/modules/products/types/product-api.types').ApiPagination | undefined;

        // Append, deduplicating by id
        const existingIds = new Set(this.products.map((p) => p.id));
        const newItems = rawItems
          .map((item) => apiProductToViewModel(item))
          .filter((p) => !existingIds.has(p.id));

        this.products.push(...newItems);
        this.total = pagination?.total ?? this.total;
        this.currentPage = pagination?.page ?? nextPage;
        this.hasMore = pagination ? pagination.page < pagination.totalPages : false;

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to load more products');
        return false;
      } finally {
        this.isLoadingMore = false;
      }
    },

    // -------------------------------------------------------------------------
    // Fetch single product profile
    // -------------------------------------------------------------------------

    /**
     * Fetch the full profile for a single product (includes attributes + allergens).
     * Stores the raw ApiProductDetail in `profile` and syncs the matching list entry.
     */
    async fetchProductProfile(id: string): Promise<boolean> {
      this.isLoadingProfile = true;
      this.profileError = null;

      try {
        const response = await apiFetchProductById(id);
        // response.data is the unwrapped ApiProductDetail (createApiClient strips envelope)
        this.profile = response.data;

        // Keep the list entry in sync (if present)
        const vm = apiProductToViewModel(this.profile);
        const idx = this.products.findIndex((p) => p.id === id);
        if (idx !== -1) {
          this.products[idx] = vm;
        }

        return true;
      } catch (err) {
        this.profileError = getApiErrorMessage(err, 'Failed to fetch product details');
        return false;
      } finally {
        this.isLoadingProfile = false;
      }
    },

    // -------------------------------------------------------------------------
    // Create product
    // -------------------------------------------------------------------------

    /**
     * Create a new product from the multi-step registration form payload.
     * The API response is a full ProductDetail. The list is refreshed from page 1.
     */
    async createProductFromForm(payload: ProductRegistrationPayload): Promise<boolean> {
      this.isCreating = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditNewValue: unknown | undefined;

      try {
        const apiBody = buildCreateProductRequest(payload);
        const response = await apiCreateProduct(apiBody);
        auditStatus = 'SUCCESS';
        auditResourceId = response.data.product_id;
        auditNewValue = apiBody;
        await this.fetchProducts({ page: 1, pageSize: this.pageSize });
        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to create product');
        auditStatus = 'FAILURE';
        auditErrorCode = 'CREATE_PRODUCT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to create product';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'CREATE_PRODUCT_FAILED';
        }
        auditNewValue = buildCreateProductRequest(payload);
        return false;
      } finally {
        this.isCreating = false;
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'REGISTER',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: undefined,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Create a product from a simple request object (legacy / view compatibility).
     * Builds a minimal API request from the view-model fields.
     */
    async createProduct(
      request: CreateProductRequest & { registrationData?: Record<string, unknown> },
    ): Promise<boolean> {
      this.isCreating = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditNewValue: unknown | undefined;

      try {
        // If a full form payload is embedded, use the full transform
        if (request.registrationData) {
          return await this.createProductFromForm(
            request.registrationData as unknown as ProductRegistrationPayload,
          );
        }

        // Minimal request from simple fields
        const apiBody = {
          sku: request.sku,
          product_name: request.description || request.brand || request.sku,
          brand_name: request.brand || undefined,
          form_name: request.form || undefined,
          manufacturer_name: request.manufacturer || undefined,
          ndc_format_code: request.ndc || undefined,
          is_active: true, // always true on create; will be handled in a future iteration
        };

        const response = await apiCreateProduct(apiBody);
        auditStatus = 'SUCCESS';
        auditResourceId = response.data.product_id;
        auditNewValue = apiBody;

        await this.fetchProducts({ page: 1, pageSize: this.pageSize });
        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to create product');
        auditStatus = 'FAILURE';
        auditErrorCode = 'CREATE_PRODUCT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to create product';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'CREATE_PRODUCT_FAILED';
        }
        auditNewValue = {
          sku: request.sku,
          product_name: request.description || request.brand || request.sku,
          brand_name: request.brand || undefined,
          form_name: request.form || undefined,
          manufacturer_name: request.manufacturer || undefined,
          ndc_format_code: request.ndc || undefined,
          is_active: true,
        };
        return false;
      } finally {
        this.isCreating = false;
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'REGISTER',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: undefined,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    // -------------------------------------------------------------------------
    // Update product
    // -------------------------------------------------------------------------

    /**
     * Partially update an existing product.
     * Accepts view-model fields and maps them to the API body.
     */
    async updateProduct(request: UpdateProductRequest): Promise<boolean> {
      this.isUpdating = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      const id = request.id;
      let existingSnapshot: Record<string, unknown> | undefined;

      if (this.profile?.product_id === id) {
        existingSnapshot = this.profile as unknown as Record<string, unknown>;
      } else {
        try {
          const profileRes = await apiFetchProductById(id);
          existingSnapshot = profileRes.data as unknown as Record<string, unknown>;
        } catch {
          // Non-critical - audit will fall back to newValue only.
        }
      }

      try {
        const { id: updateId, ...partial } = request;
        const apiBody = buildUpdateProductRequest(partial);

        const response = await apiUpdateProduct(updateId, apiBody);
        const updated = apiProductToViewModel(response.data);

        // Sync list entry
        const idx = this.products.findIndex((p) => p.id === updateId);
        if (idx !== -1) {
          this.products[idx] = updated;
        }

        // Sync profile
        if (this.profile?.product_id === updateId) {
          this.profile = response.data;
        }

        auditStatus = 'SUCCESS';
        // Identify which product this audit entry is about, regardless of which fields changed.
        auditOldValue = {
          brand_name: existingSnapshot?.brand_name ?? null,
          form_name: existingSnapshot?.form_name ?? null,
          sku: existingSnapshot?.sku ?? null,
        };
        auditNewValue = {
          brand_name: response.data.brand_name ?? null,
          form_name: response.data.form_name ?? null,
          sku: response.data.sku ?? null,
        };

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to update product');
        auditStatus = 'FAILURE';
        auditErrorCode = 'UPDATE_PRODUCT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to update product';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UPDATE_PRODUCT_FAILED';
        }
        return false;
      } finally {
        this.isUpdating = false;
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Update a product from a full form payload (used by the edit dialog).
     * Builds an UpdateProductApiRequest via `buildUpdateProductFromForm` which
     * sends `status` directly, maps allergens, and includes `needs_review`.
     */
    async updateProductFromForm(
      id: string,
      payload: ProductRegistrationPayload,
    ): Promise<boolean> {
      this.isUpdating = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      let existingSnapshot: Record<string, unknown> | undefined;
      if (this.profile?.product_id === id) {
        existingSnapshot = this.profile as unknown as Record<string, unknown>;
      } else {
        try {
          const profileRes = await apiFetchProductById(id);
          existingSnapshot = profileRes.data as unknown as Record<string, unknown>;
        } catch {
          // Non-critical - audit will fall back to newValue only.
        }
      }

      try {
        const apiBody = buildUpdateProductFromForm(payload);
        const response = await apiUpdateProduct(id, apiBody);
        const updated = apiProductToViewModel(response.data);

        const idx = this.products.findIndex((p) => p.id === id);
        if (idx !== -1) {
          this.products[idx] = updated;
        }

        if (this.profile?.product_id === id) {
          this.profile = response.data;
        }

        auditStatus = 'SUCCESS';
        // Identify which product this audit entry is about, regardless of which fields changed.
        auditOldValue = {
          brand_name: existingSnapshot?.brand_name ?? null,
          form_name: existingSnapshot?.form_name ?? null,
          sku: existingSnapshot?.sku ?? null,
        };
        auditNewValue = {
          brand_name: response.data.brand_name ?? null,
          form_name: response.data.form_name ?? null,
          sku: response.data.sku ?? null,
        };

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to update product');
        auditStatus = 'FAILURE';
        auditErrorCode = 'UPDATE_PRODUCT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to update product';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UPDATE_PRODUCT_FAILED';
        }
        return false;
      } finally {
        this.isUpdating = false;
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Convert the currently loaded profile (ApiProductDetail) into a
     * ProductRegistrationPayload suitable for the edit form.
     * Returns null if no profile is loaded.
     */
    getFormPayloadFromProfile(): ProductRegistrationPayload | null {
      if (!this.profile) return null;
      return apiDetailToFormPayload(this.profile);
    },

    // -------------------------------------------------------------------------
    // Delete / archive product
    // NOTE: The API reference has no DELETE endpoint; soft-delete via PATCH status.
    // -------------------------------------------------------------------------

    /** Soft-delete a product by archiving it (PATCH status = archived).
     *  Sends the complete product payload so the backend receives all mandatory fields. */
    async deleteProduct(id: string): Promise<boolean> {
      this.isUpdating = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      try {
        // Ensure we have the full profile; fetch it if not already loaded.
        let detail: ApiProductDetail | null =
          this.profile?.product_id === id ? this.profile : null;

        if (!detail) {
          const profileRes = await apiFetchProductById(id);
          detail = profileRes.data;
        }

        auditOldValue = {
          status: detail.status,
          is_active: detail.is_active,
          brand_name: detail.brand_name ?? null,
          form_name: detail.form_name ?? null,
          sku: detail.sku ?? null,
        };

        // Convert full profile → form payload → full API request body
        const formPayload = apiDetailToFormPayload(detail);
        const body = buildUpdateProductFromForm(formPayload);

        // Override status to 'archived'
        body.status = 'archived';
        body.is_active = true;

        auditNewValue = {
          status: body.status,
          is_active: body.is_active,
          brand_name: detail.brand_name ?? null,
          form_name: detail.form_name ?? null,
          sku: detail.sku ?? null,
        };

        const response = await apiUpdateProduct(id, body);
        const updated = apiProductToViewModel(response.data);

        // Keep the product in the list but reflect the server's returned state
        const idx = this.products.findIndex((p) => p.id === id);
        if (idx !== -1) {
          this.products[idx] = updated;
        }
        this.selectedProductIds.delete(id);

        if (this.profile?.product_id === id) {
          this.profile = null;
        }

        auditStatus = 'SUCCESS';

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to delete product');
        auditStatus = 'FAILURE';
        auditErrorCode = 'DELETE_PRODUCT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to delete product';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'DELETE_PRODUCT_FAILED';
        }
        return false;
      } finally {
        this.isUpdating = false;
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    // -------------------------------------------------------------------------
    // Upload product image
    // -------------------------------------------------------------------------

    /** Upload a new image for the given product, update the in-memory record. */
    async uploadImage(id: string, file: File): Promise<string | null> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditNewValue: unknown | undefined;

      const existingImageUrl =
        this.profile?.product_id === id
          ? this.profile.image_url
          : this.products.find((p) => p.id === id)?.imageUrl;
      const auditOldValue: unknown = { image_url: existingImageUrl ?? null };

      try {
        const response = await apiUploadProductImage(id, file);
        const imageUrl = response.data.image_url;
        auditStatus = 'SUCCESS';
        auditNewValue = { image_url: imageUrl };

        const idx = this.products.findIndex((p) => p.id === id);
        if (idx !== -1) {
          this.products[idx] = { ...this.products[idx], imageUrl };
        }

        if (this.profile?.product_id === id) {
          this.profile = { ...this.profile, image_url: imageUrl };
        }

        return imageUrl;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Failed to upload image');
        auditStatus = 'FAILURE';
        auditErrorCode = 'UPLOAD_PRODUCT_IMAGE_FAILED';
        auditErrorMessage = this.error ?? 'Failed to upload image';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UPLOAD_PRODUCT_IMAGE_FAILED';
        }
        return null;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'PRODUCT',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'products',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    // -------------------------------------------------------------------------
    // Lookups
    // -------------------------------------------------------------------------

    /**
     * Fetch all four lookup reference tables in parallel.
     * Results populate availableBrands / availableForms / etc. getters.
     *
     * Merges with any locally-created items (from createLookupItem) so that
     * items created while the fetch was in-flight are not lost.
     */
    async fetchAllLookups(): Promise<void> {
      this.isLoadingLookups = true;

      try {
        const [brands, categories, forms, manufacturers] = await Promise.allSettled([
          apiFetchLookups('brand'),
          apiFetchLookups('category'),
          apiFetchLookups('form'),
          apiFetchLookups('manufacturer'),
        ]);

        // Merge fetched data with any locally-created items not yet in the response
        const merge = (
          fetched: ApiLookupItem[],
          existing: ApiLookupItem[],
        ): ApiLookupItem[] => {
          const ids = new Set(fetched.map((item) => item.id));
          const extras = existing.filter((item) => !ids.has(item.id));
          return [...fetched, ...extras];
        };

        if (brands.status === 'fulfilled')
          this.lookups.brands = merge(brands.value.data, this.lookups.brands);
        if (categories.status === 'fulfilled')
          this.lookups.categories = merge(categories.value.data, this.lookups.categories);
        if (forms.status === 'fulfilled')
          this.lookups.forms = merge(forms.value.data, this.lookups.forms);
        if (manufacturers.status === 'fulfilled')
          this.lookups.manufacturers = merge(manufacturers.value.data, this.lookups.manufacturers);
      } catch {
        // allSettled handles individual failures — overall failure is non-fatal
      } finally {
        this.isLoadingLookups = false;
      }
    },

    /**
     * Fetch brands filtered by manufacturer name.
     * Replaces the current brands list with the result.
     */
    async fetchBrandsByManufacturer(manufacturerName: string): Promise<void> {
      this.isLoadingBrands = true;
      try {
        const response = await apiFetchLookups('brand', manufacturerName);
        this.lookups.brands = response.data;
      } catch {
        // API call failed (e.g. new manufacturer not on backend yet) — clear
        // stale brands so the user doesn't see options from a previous manufacturer.
        this.lookups.brands = [];
      } finally {
        this.isLoadingBrands = false;
      }
    },



    /**
     * Add a lookup item to the local state only (no API call).
     * Used for brand and manufacturer — the backend creates these
     * automatically when the product is saved via the create/update API.
     */
    addLocalLookupItem(
      type: 'brand' | 'manufacturer',
      name: string,
    ): string {
      const key = type === 'brand' ? 'brands' : 'manufacturers';
      const trimmed = name.trim();

      // Avoid duplicate entries (case-insensitive)
      const exists = this.lookups[key].some(
        (item) => item.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (!exists) {
        this.lookups[key].push({
          id: `local-${Date.now()}`,
          name: trimmed,
          created_at: new Date().toISOString(),
        });
      }

      return trimmed;
    },

    // -------------------------------------------------------------------------
    // Selection helpers
    // -------------------------------------------------------------------------

    toggleProductSelection(id: string) {
      if (this.selectedProductIds.has(id)) {
        this.selectedProductIds.delete(id);
      } else {
        this.selectedProductIds.add(id);
      }
    },

    selectAllProducts() {
      this.products.forEach((p) => this.selectedProductIds.add(p.id));
    },

    deselectAllProducts() {
      this.selectedProductIds.clear();
    },

    /** Clear the cached profile (call when leaving the detail page). */
    clearProfile() {
      this.profile = null;
      this.profileError = null;
    },
  },
});
